import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AiChatContainer from "@/components/ai/ai-chat-container";
import AiSideBar from "@/components/ai/ai-side-bar";
import ChatMemoButton from "@/components/chat-memo/chat-memo-button";
import ChatMemoDrawer from "@/components/chat-memo/chat-memo-drawer";
import AnswerSourceSideBar from "@/components/message/answer-source-side-bar";
import type { ChatMemo } from "@/components/chat-memo/types";
import { useChatMemosQuery } from "@/hooks/services/queries/ai-agent/use-chat-memos-query";
import { useCreateChatMemoMutation } from "@/hooks/services/mutations/chat-memo/use-create-chat-memo-mutation";
import { useUpdateChatMemoMutation } from "@/hooks/services/mutations/chat-memo/use-update-chat-memo-mutation";
import { useDeleteChatMemoMutation } from "@/hooks/services/mutations/chat-memo/use-delete-chat-memo-mutation";
import { useChatsQuery } from "@/hooks/services/queries/ai-agent/use-chats-query";
import useChatMemoStore from "@/stores/chat-memo";
import useAnswerSideBarStore from "@/stores/answer-side-bar";
import Spinner from "@/components/shared/spinner";

const AiChatPage = () => {
  const { id } = useParams();
  const chatId = id || "";
  const queryClient = useQueryClient();

  const isOpen = useChatMemoStore((state) => state.isOpen);
  const openChatMemo = useChatMemoStore((state) => state.openChatMemo);
  const closeChatMemo = useChatMemoStore((state) => state.closeChatMemo);
  const isLoading = useChatMemoStore((state) => state.isLoading);

  const answerSideBarIsOpen = useAnswerSideBarStore((state) => state.isOpen);
  const closeAnswerSideBar = useAnswerSideBarStore(
    (state) => state.closeAnswerSideBar
  );

  const { data: chats } = useChatsQuery();

  // 이전 상태 추적
  const prevAnswerSideBarIsOpen = useRef(answerSideBarIsOpen);
  const prevIsOpen = useRef(isOpen);

  // 사이드바 상호 배타적 열림 처리 - AnswerSourceSideBar가 새로 열리면 ChatMemoDrawer 닫기
  useEffect(() => {
    if (answerSideBarIsOpen && !prevAnswerSideBarIsOpen.current && isOpen) {
      closeChatMemo();
    }
    prevAnswerSideBarIsOpen.current = answerSideBarIsOpen;
  }, [answerSideBarIsOpen, isOpen, closeChatMemo]);

  // ChatMemoDrawer가 새로 열리면 AnswerSourceSideBar 닫기
  useEffect(() => {
    if (isOpen && !prevIsOpen.current && answerSideBarIsOpen) {
      closeAnswerSideBar();
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, answerSideBarIsOpen, closeAnswerSideBar]);

  const isSidebarOpen = isOpen || answerSideBarIsOpen;

  const currentChat = chats?.content?.find((chat) => chat.chatId === chatId);

  const { data: memos } = useChatMemosQuery({ chatId });
  const createMemoMutation = useCreateChatMemoMutation();
  const updateMemoMutation = useUpdateChatMemoMutation();
  const deleteMemoMutation = useDeleteChatMemoMutation();

  const handleDeleteMemo = (memoId: string) => {
    if (!chatId) return;

    deleteMemoMutation.mutate(
      { chatId, memoId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["chat-memos", chatId] });
        },
      }
    );
  };

  const handleEditMemo = (editedMemo: ChatMemo) => {
    if (!chatId) return;

    const existingMemo = memos.content.find(
      (memo: ChatMemo) => memo.id === editedMemo.id
    );

    if (existingMemo) {
      // 기존 메모 업데이트
      updateMemoMutation.mutate(
        {
          chatId,
          memoId: editedMemo.id,
          title: editedMemo.title || "Untitled",
          content: editedMemo.content,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["chat-memos", chatId] });
          },
        }
      );
    } else {
      // 새 메모 생성 (저장된 경우에만)
      if (editedMemo.title || editedMemo.content) {
        createMemoMutation.mutate(
          {
            chatId,
            title: editedMemo.title || "Untitled",
            content: editedMemo.content,
            chatHistoryId: editedMemo.sourceMessageId,
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: ["chat-memos", chatId],
              });
            },
          }
        );
      }
    }
  };

  return (
    <div
      className="flex h-[calc(100vh-70px)] overflow-hidden"
      style={{
        background:
          "radial-gradient(50.44% 50.44% at 50.87% 49.25%, #C7EFEB 0%, #FFF 100%)",
      }}
    >
      <AiSideBar />
      <div className="flex-1 flex min-w-0">
        <AiChatContainer sidebarOpen={isSidebarOpen} />
        <AnswerSourceSideBar />
      </div>
      <ChatMemoDrawer
        isOpen={isOpen}
        onClose={closeChatMemo}
        memos={memos?.content || []}
        onDeleteMemo={handleDeleteMemo}
        onEditMemo={handleEditMemo}
        chatTitle={currentChat?.chatTitle || ""}
      />
      <ChatMemoButton
        onOpen={openChatMemo}
        isOpen={isOpen}
        sidebarOpen={isSidebarOpen}
      />
      {isLoading && (
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default AiChatPage;
